/**
 * Meta Pixel (browser) + Conversions API (server) bridge.
 *
 * Every conversion event is sent BOTH ways with the same `event_id`
 * so Meta de-duplicates across the two paths. Why both paths?
 *   - Browser pixel: rich client-side signal (UA, IP, full cookies),
 *     drops on iOS 14.5+ ATT, ad-blockers, and tracking-prevention.
 *   - Server CAPI: survives all of the above, but lacks the client-
 *     side cookies/signals unless we pass them through.
 *
 * We also POST the same event to our own `/api/meta-events` so the
 * admin dashboard can render funnel counts without round-tripping
 * the Meta Graph API.
 */
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let pixelInitialized = false;

// ---------- pixel installer (unchanged behaviour) ---------------------------

function createFbqStub() {
  if (window.fbq) return window.fbq;
  const fbq = function (...args) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
}

function injectPixelScript() {
  if (document.getElementById("meta-pixel-script")) return;
  const script = document.createElement("script");
  script.id = "meta-pixel-script";
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

export function initMetaPixel() {
  if (typeof window === "undefined") return;
  if (pixelInitialized) return;
  if (!PIXEL_ID) return;
  pixelInitialized = true;
  const fbq = createFbqStub();
  injectPixelScript();
  fbq("init", PIXEL_ID);
}

// ---------- event_id + cookie helpers ---------------------------------------

function newEventId() {
  // Crypto.randomUUID is available in all modern browsers; fall back
  // to a tiny generator for older builds. Format doesn't matter to
  // Meta — it just needs to match what we send via CAPI.
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "ev_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function readFbp() {
  return readCookie("_fbp");
}

/**
 * Returns the _fbc cookie value Meta expects. If the page came in via
 * a paid Facebook click, the URL carries `fbclid=…` and Meta's pixel
 * normally writes the cookie itself. We synthesise it from the URL
 * param on first load in case the pixel hasn't fired yet so CAPI gets
 * the click attribution on the very first event.
 */
function readFbc() {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (!fbclid) return null;
    return `fb.1.${Date.now()}.${fbclid}`;
  } catch {
    return null;
  }
}

// ---------- the unified tracker ---------------------------------------------

async function sendToBackend(eventName, eventId, customData, userEmail) {
  if (!BACKEND_URL) return;
  try {
    await fetch(`${BACKEND_URL}/api/meta-events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive: true,    // survives page-unload (e.g. Subscribe redirect)
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        custom_data: customData || null,
        user_data: {
          email: userEmail || null,
          fbp: readFbp(),
          fbc: readFbc(),
        },
      }),
    });
  } catch {
    // Soft-fail — we already fired the browser pixel, so CAPI/admin
    // dropping silently is acceptable; the conversion is still
    // counted via the pixel.
  }
}

function fireBoth(eventName, customData, userEmail) {
  const eventId = newEventId();
  if (typeof window !== "undefined" && window.fbq) {
    // The `eventID` option (capital ID) is what Meta uses to dedupe
    // browser↔server pairs; spelling matters.
    window.fbq("track", eventName, customData || {}, { eventID: eventId });
  }
  // Don't await — backend ping shouldn't block the UI.
  sendToBackend(eventName, eventId, customData, userEmail);
  return eventId;
}

// PageView is high-frequency and noisy; we always pair it (Meta
// optimises against it for retargeting) but with no custom_data.
export function trackPageView() {
  return fireBoth("PageView", null, null);
}

export function trackLead(customData = {}, userEmail) {
  return fireBoth("Lead", customData, userEmail);
}

export function trackCompleteRegistration(customData = {}, userEmail) {
  return fireBoth("CompleteRegistration", customData, userEmail);
}

export function trackSubscribe(customData = {}, userEmail) {
  return fireBoth("Subscribe", customData, userEmail);
}

export function trackInitiateCheckout(customData = {}, userEmail) {
  return fireBoth("InitiateCheckout", customData, userEmail);
}
